
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';
import { ensureVezgoAccountId } from './type-helpers';
import { ensureValidToken } from '@/services/AuthAwareApiService';

// Interface for VezgoConnection to provide strong typing
interface VezgoConnection {
  id: string;
  user_id: string;
  vezgo_account_id: string;
  connection_status: string;
  created_at: string;
  updated_at: string;
  error_code: string | null;
  error_message: string | null;
  metadata?: Record<string, any>;
}

/**
 * Checks if a connection already exists for the given account ID
 * This helps prevent duplicate connections from being created
 */
export async function checkExistingConnection(accountId: string): Promise<VezgoConnection | null> {
  try {
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      logger.error('checkExistingConnection', 'No valid session', {
        error: sessionError?.message,
        accountId,
        timestamp: new Date().toISOString()
      });
      return null;
    }
    
    // Query for existing connection with this account ID for the current user
    const { data, error } = await supabase
      .from('vezgo_connections')
      .select('*')
      .eq('user_id', session.user.id)
      .eq('vezgo_account_id', accountId)
      .order('updated_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    
    if (error) {
      logger.error('checkExistingConnection', 'Error checking for existing connection', {
        error: error.message,
        details: error,
        accountId,
        timestamp: new Date().toISOString()
      });
      return null;
    }
    
    // Return the existing connection if found
    return data as VezgoConnection | null;
  } catch (err) {
    logger.error('checkExistingConnection', 'Exception during connection check', {
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
      accountId,
      timestamp: new Date().toISOString()
    });
    return null;
  }
}

// Define type for options
interface SaveConnectionOptions {
  code?: string | null;
  metadata?: Record<string, any>;
  provider?: string;
}

export async function saveConnectionToDB(
  accountId: string,
  options?: SaveConnectionOptions
): Promise<boolean> {
  // DEBUG LOGS - To verify this code is running
  console.log('*****************************************');
  console.log('VEZGO DEBUG: Running updated saveConnectionToDB function with body parameters');
  console.log(`VEZGO DEBUG: Account ID: ${accountId}`);
  console.log(`VEZGO DEBUG: Provider: ${options?.provider || 'vezgo'}`); // Add provider logging
  console.log('*****************************************');

  const startTime = Date.now();
  
  logger.info('saveConnectionToDB', 'Function called with account ID', {
    accountId,
    hasCode: !!options?.code,
    provider: options?.provider || 'vezgo', // Explicitly log provider
    options,
    timestamp: new Date().toISOString()
  });

  try {
    // Ensure accountId is a valid string
    accountId = ensureVezgoAccountId(accountId);
    
    if (!accountId) {
      logger.error('saveConnectionToDB', 'Invalid account ID provided', {
        accountId,
        timestamp: new Date().toISOString()
      });
      return false;
    }

    const sessionStartTime = Date.now();
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    const sessionEndTime = Date.now();
    
    logger.debug('saveConnectionToDB', 'Retrieved session', {
      sessionDuration: `${sessionEndTime - sessionStartTime}ms`,
      hasError: !!sessionError,
      timestamp: new Date().toISOString()
    });

    if (sessionError || !session) {
      logger.error('saveConnectionToDB', 'No authenticated user session found', {
        timestamp: new Date().toISOString()
      });
      return false;
    }

    // URL-ONLY APPROACH: Build URL parameters with proper snake_case naming
    const urlParams = new URLSearchParams();
    urlParams.append('vezgo_account_id', accountId);
    if (options?.code) urlParams.append('code', options.code);
    
    // CRITICAL FIX: Always include provider parameter
    const provider = options?.provider || 'vezgo';
    urlParams.append('provider', provider);
    
    // Detailed debug logging
    console.log('VEZGO DEBUG: Calling vezgo-save-connection with URL parameters:');
    console.log('- URL params:', urlParams.toString());
    console.log('- Provider explicitly set to:', provider);
    
    logger.info('saveConnectionToDB', 'Using URL parameters approach', { 
      vezgo_account_id: accountId,
      provider: provider, // Explicitly log the provider
      url_params: urlParams.toString(),
      timestamp: new Date().toISOString() 
    });

    const edgeFunctionStartTime = Date.now();
    
    // Ensure valid token before making API call
    await ensureValidToken('vezgo-save-connection');
    
    // Make the API call with ONLY URL parameters
    const { data, error } = await supabase.functions.invoke(
      `vezgo-save-connection?${urlParams.toString()}`,
      {
        // Empty body - all parameters are in the URL
        body: {},
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    const edgeFunctionEndTime = Date.now();
    
    // Debug logging for response
    console.log('VEZGO DEBUG: Edge function response received:', error ? 'ERROR' : 'SUCCESS');
    if (error) console.error('VEZGO DEBUG: Error details:', error);
    if (data) console.log('VEZGO DEBUG: Response data:', data);
    
    // Log the response details
    logger.debug('saveConnectionToDB', 'Edge function response received', {
      duration: `${edgeFunctionEndTime - edgeFunctionStartTime}ms`,
      success: !error && data?.success,
      hasError: !!error,
      responseData: data,
      timestamp: new Date().toISOString()
    });
    
    // Handle errors
    if (error || (data && !data.success)) {
      logger.error('saveConnectionToDB', 'Edge function error', { 
        error: error || data?.error,
        details: data,
        requestDuration: `${edgeFunctionEndTime - edgeFunctionStartTime}ms`,
        timestamp: new Date().toISOString()
      });
      return false;
    }
    
    // Success path
    logger.info('saveConnectionToDB', 'Connection saved successfully', {
      accountId,
      userId: session.user.id,
      provider: provider, // Log provider in success case
      totalDuration: `${Date.now() - startTime}ms`,
      edgeFunctionDuration: `${edgeFunctionEndTime - edgeFunctionStartTime}ms`,
      timestamp: new Date().toISOString()
    });
    
    return true;
  } catch (err) {
    // Single catch block for all errors
    console.error('VEZGO DEBUG: Unexpected error in saveConnectionToDB:', err);
    logger.error('saveConnectionToDB', 'Unexpected error', {
      error: err instanceof Error ? err.message : String(err),
      stack: err instanceof Error ? err.stack : undefined,
      accountId,
      provider: options?.provider, // Log provider in error case
      options,
      duration: `${Date.now() - startTime}ms`,
      timestamp: new Date().toISOString()
    });
    return false;
  }
}
